import { Link, createFileRoute } from '@tanstack/react-router';
import { Activity, Mail, ShieldCheck, User } from 'lucide-react';
import { useSession } from '@/lib/auth';
import { hasPermission } from '@/lib/permissions';
import { roleLabelTh } from '@/lib/role-label';
import { LoginActivitySection } from '@/components/account/login-activity-section';
import { SessionsSection } from '@/components/account/sessions-section';
import { TwoFactorSection } from '@/components/account/two-factor-section';

interface AccountSearch {
  // Deep-link control. `setup=2fa` arrives from the Required2faBanner
  // click — TwoFactorSection auto-opens its setup dialog so the user
  // lands on the QR code, not a wall of cards they have to scan
  // through to find the toggle. Param is stripped after first fire so
  // closing the dialog + refreshing doesn't re-trigger.
  setup?: '2fa';
}

export const Route = createFileRoute('/_authenticated/account')({
  validateSearch: (search: Record<string, unknown>): AccountSearch => ({
    setup: search.setup === '2fa' ? '2fa' : undefined,
  }),
  component: AccountPage,
});

function AccountPage() {
  const { user } = useSession();
  const { setup } = Route.useSearch();
  const canReadAudit = hasPermission(user, 'audit.read');

  return (
    <div className="p-4 sm:p-8">
      <div className="mx-auto max-w-3xl space-y-6">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="bg-brand-100 text-brand-700 dark:bg-brand-900/30 dark:text-brand-400 flex h-10 w-10 items-center justify-center rounded-full">
              <User className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-50">
                บัญชีของฉัน
              </h1>
              <p className="mt-0.5 text-sm text-slate-500">จัดการการเข้าสู่ระบบและความปลอดภัย</p>
            </div>
          </div>
          {/* Self-audit shortcut for audit.read holders — answers "what
              have I done lately across the whole system?" without
              composing the URL by hand. LoginActivitySection below is
              limited to auth events only; this drills to everything. */}
          {canReadAudit && user?.id && (
            <Link
              to="/audit"
              search={{ userId: user.id }}
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
              title="ดู audit ทั้งหมดของฉัน"
            >
              <Activity className="h-3.5 w-3.5" />
              ดู audit ของฉัน
            </Link>
          )}
        </div>

        {/* Profile preview — read-only here. Full edit is on /users page for admins. */}
        <section className="rounded-lg border border-slate-200 bg-white p-6 dark:border-slate-800 dark:bg-slate-900">
          <h3 className="mb-4 text-base font-semibold text-slate-900 dark:text-slate-50">
            โปรไฟล์
          </h3>
          <dl className="space-y-3 text-sm">
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-slate-400" />
              <dt className="w-24 text-slate-500">อีเมล</dt>
              <dd className="text-slate-900 dark:text-slate-100">{user?.email}</dd>
            </div>
            <div className="flex items-center gap-3">
              <User className="h-4 w-4 text-slate-400" />
              <dt className="w-24 text-slate-500">ชื่อ</dt>
              <dd className="text-slate-900 dark:text-slate-100">
                {user?.name}{' '}
                <span className="ml-1 font-mono text-xs text-slate-400">@{user?.username}</span>
              </dd>
            </div>
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-4 w-4 text-slate-400" />
              <dt className="w-24 text-slate-500">Role</dt>
              <dd className="text-slate-900 dark:text-slate-100">{roleLabelTh(user?.role.slug)}</dd>
            </div>
          </dl>
        </section>

        <TwoFactorSection autoOpenSetup={setup === '2fa'} />
        <SessionsSection />
        <LoginActivitySection />
      </div>
    </div>
  );
}
